import {filterPagesByTitle, hasLiveVersion, hasPendingEdit, inferLayoutContexts, recordJustPublishedPaths, toEditFormData, toLayoutPreviewData, toPreviewData, toPublishFormData} from '../../../core/page/page.core.js';
import {t, tpl} from '../../../i18n/i18n.js';
import {DEFAULT_LAYOUT_CONTEXT, ErrorCode, NotificationType, PublishJobStatus, PublishStage, RenderMode, StagedOperation} from '../../../core/vocabulary.js';
import {errorText} from './error-text.js';

/** Page size that fetches every page at once, for lookups over the whole site. */
const ALL_PAGES = 10000;

/**
 * How a confirmation names a page: its title, or its path when it has no title (as a staged
 * draft can), like the page search does.
 * @param {string | undefined} title
 * @param {string | undefined} path
 * @returns {string}
 */
function pageLabel(title, path) {
    return (title ?? '').trim() || path || '';
}

export class PageEventAdapter {
    #pageApiAdapter;
    #templatePickerComponent;
    #pageFormComponent;
    #pageListComponent;
    #notificationComponent;
    #onSessionExpired;
    #loadTemplatesPromise = null;
    #layoutConflictCache = null;
    #layoutConflictPromise = null;
    #layoutConflictGeneration = 0;
    #previewRequestId = 0;
    #layoutPreviewRequestIds = new Map();
    #submitInFlight = false;
    #assetUrlCache = new Map();
    #assetUrlInFlight = new Map();
    #currentFilter = '';
    #currentSearch = '';
    #loadPagesRequestId = 0;
    /** Bumped by every form load; work started for an earlier form leaves the current one alone. */
    #formLoadId = 0;
    /** Every page of the last search fetch, with its filter and sort, reused while only the term changes. */
    #searchSource = null;

    #inferLayoutContexts(detail) {
        const contexts = inferLayoutContexts(detail?.fields || [], detail?.layout || null);
        return contexts.length > 0 ? contexts : [null];
    }

    /**
     * @param {import('../../adapters/driven/page.api-adapter.js').PageApiAdapter} pageApiAdapter
     * @param {HTMLElement} templatePickerComponent
     * @param {HTMLElement} pageFormComponent
     * @param {HTMLElement} pageListComponent
     * @param {HTMLElement} notificationComponent
     * @param {() => void} onSessionExpired signs out once a request finds the session expired, so
     *     the login screen shows
     */
    constructor(pageApiAdapter, templatePickerComponent, pageFormComponent, pageListComponent, notificationComponent, onSessionExpired) {
        this.#pageApiAdapter = pageApiAdapter;
        this.#templatePickerComponent = templatePickerComponent;
        this.#pageFormComponent = pageFormComponent;
        this.#pageListComponent = pageListComponent;
        this.#notificationComponent = notificationComponent;
        this.#onSessionExpired = onSessionExpired;
    }

    connect() {
        document.addEventListener('template-select', this.#onTemplateSelect);
        document.addEventListener('page-submit', this.#onPageSubmit);
        document.addEventListener('page-preview', this.#onPagePreview);
        document.addEventListener('layout-preview', this.#onLayoutPreview);
        document.addEventListener('page-edit', this.#onPageEdit);
        document.addEventListener('page-delete', this.#onPageDelete);
        document.addEventListener('page-discard', this.#onPageDiscard);
        document.addEventListener('page-publish-staged', this.#onPublishStaged);
        document.addEventListener('preview-link-clicked', this.#onPreviewLinkClicked);
        document.addEventListener('asset-request', this.#onAssetRequest);
        document.addEventListener('asset-open-request', this.#onAssetOpenRequest);
        document.addEventListener('pages-request', this.#onPagesRequest);
        document.addEventListener('templates-request', this.#onTemplatesRequest);
    }

    disconnect() {
        document.removeEventListener('template-select', this.#onTemplateSelect);
        document.removeEventListener('page-submit', this.#onPageSubmit);
        document.removeEventListener('page-preview', this.#onPagePreview);
        document.removeEventListener('layout-preview', this.#onLayoutPreview);
        document.removeEventListener('page-edit', this.#onPageEdit);
        document.removeEventListener('page-delete', this.#onPageDelete);
        document.removeEventListener('page-discard', this.#onPageDiscard);
        document.removeEventListener('page-publish-staged', this.#onPublishStaged);
        document.removeEventListener('preview-link-clicked', this.#onPreviewLinkClicked);
        document.removeEventListener('asset-request', this.#onAssetRequest);
        document.removeEventListener('asset-open-request', this.#onAssetOpenRequest);
        document.removeEventListener('pages-request', this.#onPagesRequest);
        document.removeEventListener('templates-request', this.#onTemplatesRequest);
        this.#clearAssetUrlCache();
    }

    /** @param {import('../../adapters/driven/page.api-adapter.js').PageApiAdapter} pageApiAdapter */
    updateApiAdapter(pageApiAdapter) {
        this.#pageApiAdapter = pageApiAdapter;
    }

    async loadTemplates() {
        if (this.#loadTemplatesPromise) {
            return this.#loadTemplatesPromise;
        }

        this.#templatePickerComponent.loading = true;
        this.#loadTemplatesPromise = (async () => {
            try {
                const templates = await this.#pageApiAdapter.getTemplates();
                let templatesWithAvailability;

                try {
                    const pageSummary = await this.#pageApiAdapter.getPages('', 0, ALL_PAGES);
                    const pages = Array.isArray(pageSummary?.pages) ? pageSummary.pages : [];
                    templatesWithAvailability = templates.map(template => {
                        const templatePages = pages.filter(page => page.template === template.name);
                        // A published page wins over a staged one so "edit" opens the live page.
                        const existing = templatePages.find(page => page.published) || templatePages[0];
                        const isSingle = Boolean(template.singleInstance);
                        return {
                            ...template,
                            unavailableForCreate: isSingle && templatePages.length > 0,
                            existingPage: isSingle && existing
                                ? {path: existing.path, published: Boolean(existing.published), live: hasLiveVersion(existing), pending: hasPendingEdit(existing)}
                                : null,
                            pageCount: templatePages.length
                        };
                    });
                } catch (e) {
                    console.warn('Failed to load page availability; leaving template availability unchanged', e);
                    templatesWithAvailability = templates.map(template => ({
                        ...template,
                        unavailableForCreate: false,
                        existingPage: null
                    }));
                    this.#notificationComponent.show(t('pageList.templateAvailabilityUnknown'), NotificationType.WARNING);
                }

                this.#templatePickerComponent.templates = templatesWithAvailability;
                this.#pageListComponent.templates = templatesWithAvailability;
                this.#notificationComponent.dismissError();
            } catch (error) {
                this.#templatePickerComponent.loadFailed = true;
                this.#handleError(error);
            } finally {
                this.#templatePickerComponent.loading = false;
                this.#loadTemplatesPromise = null;
            }
        })();

        return this.#loadTemplatesPromise;
    }

    async loadTemplateDetail(templateName) {
        const loadId = ++this.#formLoadId;
        const isCurrent = () => loadId === this.#formLoadId;
        this.#pageFormComponent.loadingMode = 'loading';
        this.#pageFormComponent.loading = true;
        try {
            this.#pageFormComponent.pagePath = '';
            const detail = await this.#pageApiAdapter.getTemplate(templateName);
            if (!isCurrent()) {
                return;
            }
            this.#pageFormComponent.content = detail.resolvedContent || detail.content || '';
            this.#pageFormComponent.fields = detail.fields;
            this.#pageFormComponent.description = detail.description;
            this.#pageFormComponent.slugPrefix = detail.slugPrefix || null;
            this.#pageFormComponent.homepage = Boolean(detail.homepage);
            this.#pageFormComponent.layoutConfig = detail.layout || null;
            this.#pageFormComponent.mode = detail.mode || RenderMode.PAGE;
            this.#pageFormComponent.composition = detail.composition || null;
            await this.#loadLayoutEntries(templateName);
            if (!isCurrent()) {
                return;
            }
            this.#notificationComponent.dismissError();
        } catch (error) {
            if (isCurrent()) {
                this.#handleError(error);
            }
        } finally {
            if (isCurrent()) {
                this.#pageFormComponent.loading = false;
                this.#pageFormComponent.loadingMode = 'submit';
            }
        }
    }

    /**
     * Loads one page of the page list. Without a sort order the list's own choice applies, so
     * a stored preference holds on every reload. A search term fetches every page of the
     * template filter at once and keeps the titles that contain it, on a single list page;
     * while only the term changes, the pages of the previous search fetch are filtered again
     * instead of fetched again. Only the latest call updates the list, so a slow response to
     * an earlier call cannot overwrite a newer one.
     * @param {string} [template]
     * @param {number} [page]
     * @param {number} [size]
     * @param {import('../../../core/vocabulary.js').PageSort} [sort]
     * @param {string} [search]
     */
    async loadPages(template = this.#currentFilter, page = 0, size = 10, sort = this.#pageListComponent.sortBy, search = this.#currentSearch) {
        const termChanged = search !== this.#currentSearch;
        this.#currentFilter = template;
        this.#currentSearch = search;
        const requestId = ++this.#loadPagesRequestId;
        const isLatest = () => requestId === this.#loadPagesRequestId;
        this.#pageListComponent.loadingMode = 'loading';
        this.#pageListComponent.loading = true;
        try {
            const searching = search.trim().length > 0;
            const fetched = searching
                ? await this.#searchSourcePages(template, sort, termChanged)
                : await this.#pageApiAdapter.getPages(template, page, size, sort);
            if (!isLatest()) {
                return;
            }
            this.#searchSource = searching ? {template, sort, response: fetched} : null;
            const response = searching ? this.#searchResult(fetched, search) : fetched;
            let stagedLayoutConflictPaths = [];
            let stagedLayoutMovedPaths = [];
            let layoutConflictCheckFailed = false;
            if (response.stagedDrafts) {
                const layoutCheck = await this.#loadStagedLayoutConflicts();
                if (!isLatest()) {
                    return;
                }
                stagedLayoutConflictPaths = layoutCheck.paths;
                stagedLayoutMovedPaths = layoutCheck.movedPaths ?? [];
                layoutConflictCheckFailed = layoutCheck.checkFailed;
            }
            this.#pageListComponent.stagedLayoutConflictPaths = stagedLayoutConflictPaths;
            this.#pageListComponent.stagedLayoutMovedPaths = stagedLayoutMovedPaths;
            this.#pageListComponent.layoutConflictCheckFailed = layoutConflictCheckFailed;
            this.#pageListComponent.hasStagedDrafts = response.stagedDrafts ?? false;
            this.#pageListComponent.pages = response.pages;
            this.#pageListComponent.pagination = {
                totalElements: response.totalElements,
                totalPages: response.totalPages,
                currentPage: response.currentPage
            };
            // The list is on screen again, so an error from an earlier attempt is out of date.
            this.#notificationComponent.dismissError();

            // Also ensure templates are loaded for the filter dropdown
            if (this.#pageListComponent.templates?.length === 0) {
                this.loadTemplates().catch((error) => {
                    this.#handleError(error);
                });
            }
        } catch (error) {
            if (!isLatest()) {
                return;
            }
            this.#pageListComponent.loadFailed = true;
            this.#handleError(error);
        } finally {
            if (isLatest()) {
                this.#pageListComponent.loading = false;
            }
        }
    }

    /**
     * Every page of the template filter in the given sort order. A call that only changes the
     * search term reuses the pages of the previous search fetch; any other call (a reload after
     * a change, another filter or sort) fetches them again.
     * @param {string} template
     * @param {import('../../../core/vocabulary.js').PageSort} sort
     * @param {boolean} termChanged
     */
    async #searchSourcePages(template, sort, termChanged) {
        const source = this.#searchSource;
        if (termChanged && source && source.template === template && source.sort === sort) {
            return source.response;
        }
        return this.#pageApiAdapter.getPages(template, 0, ALL_PAGES, sort);
    }

    /**
     * The pages whose title contains the search term, as one list page.
     * @param {import('../../types/user.types.js').PageListResponse} response every page of the filter
     * @param {string} search
     */
    #searchResult(response, search) {
        const pages = filterPagesByTitle(Array.isArray(response?.pages) ? response.pages : [], search);
        return {
            ...response,
            pages,
            totalElements: pages.length,
            totalPages: pages.length > 0 ? 1 : 0,
            currentPage: 0,
        };
    }

    /**
     * The layout conflicts of the staged drafts, checked once and reused until the drafts
     * change. A failed check is not kept, so the next list load checks again; a check that
     * started before the drafts changed neither fills the cache nor clears a newer check.
     */
    async #loadStagedLayoutConflicts() {
        if (this.#layoutConflictCache !== null) {
            return this.#layoutConflictCache;
        }
        if (this.#layoutConflictPromise) {
            return this.#layoutConflictPromise;
        }
        const generation = this.#layoutConflictGeneration;
        this.#layoutConflictPromise = (async () => {
            try {
                const result = await this.#checkStagedLayoutConflicts();
                if (!result.checkFailed && generation === this.#layoutConflictGeneration) {
                    this.#layoutConflictCache = result;
                }
                return result;
            } finally {
                if (generation === this.#layoutConflictGeneration) {
                    this.#layoutConflictPromise = null;
                }
            }
        })();
        return this.#layoutConflictPromise;
    }

    async #checkStagedLayoutConflicts() {
        const summary = await this.#pageApiAdapter.getPages('', 0, ALL_PAGES);
        const allPages = Array.isArray(summary?.pages) ? summary.pages : [];
        const stagedTemplates = [...new Set(
            allPages
                .filter((page) => page && page.published === false && page.template)
                .map((page) => page.template)
        )];

        if (stagedTemplates.length === 0) {
            return {paths: [], checkFailed: false};
        }

        const templateContexts = await Promise.all(
            stagedTemplates.map(async (templateName) => {
                try {
                    const detail = await this.#pageApiAdapter.getTemplate(templateName);
                    return {
                        templateName,
                        contexts: this.#inferLayoutContexts(detail)
                    };
                } catch {
                    return {
                        templateName,
                        contexts: [null],
                        detailFailed: true
                    };
                }
            })
        );

        const layoutResults = await Promise.all(
            templateContexts.flatMap(({templateName, contexts, detailFailed}) =>
                contexts.map((context) =>
                    this.#pageApiAdapter.previewLayout(templateName, null, context, {})
                        .then((preview) => ({ok: true, preview, detailFailed}))
                        .catch(() => ({ok: false, preview: null, detailFailed}))
                )
            )
        );

        const conflicts = new Set();
        const movedPaths = new Set();
        let checkFailed = false;
        for (const result of layoutResults) {
            if (result.detailFailed || !result.ok || !result.preview) {
                checkFailed = true;
                continue;
            }
            const stagedPaths = new Set((result.preview.after || [])
                .filter((entry) => entry && entry.staged)
                .flatMap((entry) => [entry.path, entry.sourcePath].filter(Boolean)));
            const hardConflicts = new Set((result.preview.hardConflicts || []).filter(Boolean));

            const touchesStaged = [...hardConflicts].some((path) => stagedPaths.has(path));
            if (touchesStaged) {
                for (const path of hardConflicts) {
                    conflicts.add(path);
                }
            }

            for (const path of (result.preview.movedPaths || []).filter(Boolean)) {
                if (!stagedPaths.has(path)) {
                    movedPaths.add(path);
                }
            }
        }
        return {paths: [...conflicts], movedPaths: [...movedPaths], checkFailed};
    }

    #invalidateLayoutConflictCache() {
        this.#layoutConflictGeneration++;
        this.#layoutConflictCache = null;
        this.#layoutConflictPromise = null;
    }

    #onTemplatesRequest = () => {
        this.loadTemplates();
    };

    #onPagesRequest = (event) => {
        const {template, page, sort, search} = event.detail;
        this.loadPages(template, page, undefined, sort, search ?? '');
    };

    /**
     * @param {string} templateName
     * @param {string} pagePath
     * @param {{live?: boolean, pending?: boolean}} [options] live=false when the page has no live version yet
     *     (new staged draft); pending=true when a live page has a staged edit that is not published yet
     */
    async loadEditPage(templateName, pagePath, {live = true, pending = false} = {}) {
        const loadId = ++this.#formLoadId;
        const isCurrent = () => loadId === this.#formLoadId;
        this.#pageFormComponent.loadingMode = 'loading';
        this.#pageFormComponent.loading = true;
        try {
            const [template, page] = await Promise.all([
                this.#pageApiAdapter.getTemplate(templateName),
                this.#pageApiAdapter.getPageDetail(pagePath),
            ]);
            if (!isCurrent()) {
                return;
            }

            this.#pageFormComponent.editMode = true;
            this.#pageFormComponent.pageLive = live;
            this.#pageFormComponent.pagePending = pending;
            this.#pageFormComponent.content = template.resolvedContent || template.content || '';
            this.#pageFormComponent.fields = template.fields;
            this.#pageFormComponent.description = template.description;
            this.#pageFormComponent.slugPrefix = template.slugPrefix || null;
            this.#pageFormComponent.homepage = Boolean(template.homepage);
            this.#pageFormComponent.layoutConfig = template.layout || null;
            this.#pageFormComponent.mode = template.mode || RenderMode.PAGE;
            this.#pageFormComponent.composition = template.composition || null;
            this.#pageFormComponent.pagePath = page.path;
            this.#pageFormComponent.prefillData = page.data;
            await this.#loadLayoutEntries(templateName);
            if (!isCurrent()) {
                return;
            }
            this.#notificationComponent.dismissError();
        } catch (error) {
            if (isCurrent()) {
                this.#handleError(error);
            }
        } finally {
            if (isCurrent()) {
                this.#pageFormComponent.loading = false;
                this.#pageFormComponent.loadingMode = 'submit';
            }
        }
    }

    #onTemplateSelect = (event) => {
        location.hash = `#/publish/${event.detail.templateName}`;
    };

    #onPagePreview = async (event) => {
        const requestId = ++this.#previewRequestId;
        const loadId = this.#formLoadId;
        const isStale = () => requestId !== this.#previewRequestId || loadId !== this.#formLoadId;
        const {templateName, formElement, fields} = event.detail;
        try {
            const data = await toPreviewData(fields, formElement, this.#pageFormComponent.layoutConfig);
            // A newer form was opened; it shows its own preview.
            if (loadId !== this.#formLoadId) {
                return;
            }
            let html = await this.#pageApiAdapter.previewPage(templateName, data);
            if (loadId !== this.#formLoadId) {
                return;
            }
            html = await this.#inlineAssets(html);
            if (isStale()) {
                return;
            }
            this.#pageFormComponent.previewContent = html;
            this.#pageFormComponent.previewLoading = false;
            // The preview refreshes as the user types, so the error of an earlier refresh is out of date.
            this.#notificationComponent.dismissError();
        } catch (error) {
            if (isStale()) {
                return;
            }
            let shownInline;
            if (typeof this.#pageFormComponent.previewFailed === 'function') {
                shownInline = this.#pageFormComponent.previewFailed() === true;
            } else {
                this.#pageFormComponent.previewLoading = false;
                shownInline = false;
            }
            // The panel labels the last preview as outdated, so a toast would only repeat it. An
            // expired session still goes through the shared handling, which signs the user out.
            if (error?.status === 401 || !shownInline) {
                this.#handleError(error);
            }
        }
    };

    #onLayoutPreview = async (event) => {
        const {templateName, pagePath, context, formElement, fields, prioritizePosition} = event.detail;
        const normalizedContext = context || DEFAULT_LAYOUT_CONTEXT;
        const requestId = (this.#layoutPreviewRequestIds.get(normalizedContext) ?? 0) + 1;
        this.#layoutPreviewRequestIds.set(normalizedContext, requestId);
        const loadId = this.#formLoadId;
        try {
            const data = toLayoutPreviewData(fields, formElement, this.#pageFormComponent.layoutConfig);
            const preview = await this.#pageApiAdapter.previewLayout(
                templateName,
                pagePath || null,
                context || null,
                data,
                Boolean(prioritizePosition)
            );
            if (requestId !== this.#layoutPreviewRequestIds.get(normalizedContext) || loadId !== this.#formLoadId) {
                return;
            }
            this.#setLayoutPreviewForContext(normalizedContext, preview);
        } catch (_error) {
            if (requestId !== this.#layoutPreviewRequestIds.get(normalizedContext) || loadId !== this.#formLoadId) {
                return;
            }
            this.#setLayoutPreviewForContext(normalizedContext, null);
        }
    };

    /** @param {string} html @returns {Promise<string>} */
    async #inlineAssets(html) {
        // Broad regex to match filenames between common delimiters (quotes, parentheses, spaces, commas, tags)
        const regex = /["'(\s,>]([^"'()\s,>]+\.(?:webp|jpg|jpeg|png|gif|pdf|svg))["')\s,<]/gi;
        const matches = [...html.matchAll(regex)];
        if (matches.length === 0) return html;

        // Extract and deduplicate relative filenames that aren't already inlined or absolute
        const uniqueFilenames = [...new Set(matches
            .map(m => m[1])
            .filter(f => !f.startsWith('data:') && !f.startsWith('http'))
        )];
        
        if (uniqueFilenames.length === 0) return html;

        const assetMap = new Map();
        await Promise.all(uniqueFilenames.map(async (filename) => {
            try {
                const assetUrl = await this.#resolveAssetUrl(filename);
                assetMap.set(filename, assetUrl);
            } catch (e) {
                console.warn(`Failed to inline asset: ${filename}`, e);
            }
        }));

        let result = html;
        // Sort by length descending to prevent partial replacements (e.g. "a.webp" inside "extra_a.webp")
        const sortedFilenames = uniqueFilenames.sort((a, b) => b.length - a.length);
        
        for (const filename of sortedFilenames) {
            const dataUrl = assetMap.get(filename);
            if (dataUrl) {
                // Precise replacement within delimiters to maintain document integrity
                const escaped = filename.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                // Use a character class for delimiters that matches the discovery regex
                const replaceRegex = new RegExp(`(["'(\\s,>])` + escaped + `(["'\\)\\s,<])`, 'g');
                result = result.replace(replaceRegex, `$1${dataUrl}$2`);
            }
        }
        return result;
    }

    /** @param {string} path @returns {Promise<string>} */
    async #resolveAssetUrl(path) {
        const cached = this.#assetUrlCache.get(path);
        if (cached) {
            return cached;
        }

        const inFlight = this.#assetUrlInFlight.get(path);
        if (inFlight) {
            return inFlight;
        }

        const promise = (async () => {
            const blob = await this.#pageApiAdapter.getAsset(path);
            const dataUrl = await this.#blobToDataUrl(blob);
            this.#assetUrlCache.set(path, dataUrl);
            return dataUrl;
        })();

        this.#assetUrlInFlight.set(path, promise);
        try {
            return await promise;
        } finally {
            this.#assetUrlInFlight.delete(path);
        }
    }

    /** @param {Blob} blob @returns {Promise<string>} */
    #blobToDataUrl(blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.addEventListener('load', () => resolve(typeof reader.result === 'string' ? reader.result : ''));
            reader.addEventListener('error', () => reject(reader.error ?? new Error('Failed to read asset')));
            reader.readAsDataURL(blob);
        });
    }

    #clearAssetUrlCache() {
        for (const url of this.#assetUrlCache.values()) {
            if (typeof url === 'string' && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        }
        this.#assetUrlCache.clear();
        this.#assetUrlInFlight.clear();
    }

    #onPageSubmit = async (event) => {
        if (this.#submitInFlight) {
            return;
        }
        this.#submitInFlight = true;
        // Lets another save through once this one is stored, even while its publication runs.
        let released = false;
        const release = () => {
            if (!released) {
                released = true;
                this.#submitInFlight = false;
            }
        };
        const loadId = this.#formLoadId;
        const isCurrent = () => loadId === this.#formLoadId;
        const {templateName, formElement, fields, editMode, pagePath, publish = false} = event.detail;
        this.#pageFormComponent.loadingMode = 'submit';
        this.#pageFormComponent.loading = true;
        try {
            let savedPath;
            if (editMode) {
                const formData = toEditFormData(fields, formElement, templateName, pagePath, this.#pageFormComponent.layoutConfig);
                savedPath = await this.#pageApiAdapter.stageEdit(formData);
                if (!publish) {
                    this.#notificationComponent.show(t('pageList.notifyEditStaged'), NotificationType.SUCCESS);
                    // Pulse the edited row on the next page-list render. New
                    // drafts have no path yet, so they are skipped.
                    if (pagePath) {
                        recordJustPublishedPaths([pagePath]);
                    }
                }
            } else {
                const formData = toPublishFormData(fields, formElement, templateName, this.#pageFormComponent.layoutConfig);
                savedPath = await this.#pageApiAdapter.stagePage(formData);
                if (!publish) {
                    this.#notificationComponent.show(t('pageList.notifyDraftSaved'), NotificationType.SUCCESS);
                }
            }
            this.#invalidateLayoutConflictCache();
            // The form's content is stored now, so leaving it is not a loss to warn about.
            if (isCurrent()) {
                this.#pageFormComponent.markSaved?.();
            }
            if (publish) {
                // The publication can take a minute. A form opened meanwhile may save, and this
                // form's own loading state keeps it from saving twice.
                release();
                await this.#publishSaved(savedPath, isCurrent);
                return;
            }
            // Another form was opened while this one saved; leave it and the route alone.
            if (!isCurrent()) {
                return;
            }
            location.hash = '#/pages';
        } catch (error) {
            // The form shows why saving failed next to its Save button, in an alert region
            // that announces it; a toast would only repeat it. An expired session still goes
            // through the shared handling, which signs the user out. A failure after another
            // form was opened has no form to show in, so it shows a toast.
            if (error?.status === 401 || !isCurrent()) {
                this.#handleError(error);
            } else {
                this.#pageFormComponent.submitError = errorText(error);
            }
        } finally {
            release();
            if (isCurrent()) {
                this.#pageFormComponent.loading = false;
                this.#pageFormComponent.loadingMode = 'submit';
            }
        }
    };

    /**
     * Publishes the draft the form just saved, and only that draft. The draft is saved either
     * way, so a failed publication still leaves the form for the page list, where the draft
     * shows, and says why it did not go live. The publication runs to its end even when another
     * form was opened meanwhile; only the form and the route are then left alone.
     * @param {string} savedPath the target path the save answered with
     * @param {() => boolean} isCurrent whether the saved form is still the open one
     * @returns {Promise<void>}
     */
    async #publishSaved(savedPath, isCurrent) {
        if (isCurrent()) {
            this.#pageFormComponent.loadingMode = 'publish';
            this.#pageFormComponent.loading = true;
        }
        try {
            if (typeof savedPath !== 'string' || !savedPath.trim()) {
                // Without an address the job would publish every draft, so nothing is published.
                throw new Error('The save did not answer with the address of the draft to publish.');
            }
            await this.#pageApiAdapter.publishStagedAsync(undefined, savedPath);
            this.#notificationComponent.show(t('pageList.notifyPublishedOne'), NotificationType.SUCCESS);
            this.#invalidateLayoutConflictCache();
            recordJustPublishedPaths([savedPath]);
            this.#announceDraftsPublished();
            if (isCurrent()) {
                location.hash = '#/pages';
            }
        } catch (error) {
            // An expired session signs the user out, so only the other failures go to the list.
            if (isCurrent() && error?.status !== 401) {
                location.hash = '#/pages';
            }
            await this.#showPublishFailure(error);
        }
    }

    /** A publication deletes the preview of the site; the site preview adapter resets its bar. */
    #announceDraftsPublished() {
        document.dispatchEvent(new CustomEvent('drafts-published'));
    }

    async #loadLayoutEntries(templateName) {
        const loadId = this.#formLoadId;
        const contexts = this.#pageFormComponent.layoutContexts?.length > 0
            ? this.#pageFormComponent.layoutContexts
            : [DEFAULT_LAYOUT_CONTEXT];

        if (typeof this.#pageFormComponent.resetLayoutData === 'function') {
            this.#pageFormComponent.resetLayoutData();
        }

        const results = await Promise.all(contexts.map(async (context) => {
            try {
                const entries = await this.#pageApiAdapter.getTemplateLayout(templateName, context === DEFAULT_LAYOUT_CONTEXT ? null : context);
                return {context, entries};
            } catch {
                return {context, entries: []};
            }
        }));
        if (loadId !== this.#formLoadId) {
            return;
        }

        for (const {context, entries} of results) {
            this.#setLayoutEntriesForContext(context, entries);
            this.#setLayoutPreviewForContext(context, null);
        }
    }

    #setLayoutEntriesForContext(context, entries) {
        if (typeof this.#pageFormComponent.setLayoutEntriesForContext === 'function') {
            this.#pageFormComponent.setLayoutEntriesForContext(context, entries);
            return;
        }
        this.#pageFormComponent.layoutEntries = entries;
    }

    #setLayoutPreviewForContext(context, preview) {
        if (typeof this.#pageFormComponent.setLayoutPreviewForContext === 'function') {
            this.#pageFormComponent.setLayoutPreviewForContext(context, preview);
            return;
        }
        this.#pageFormComponent.layoutPreview = preview;
    }

    #onPageDiscard = async (event) => {
        const {stagedId, operation, title, path} = event.detail;
        const isEdit = operation === StagedOperation.EDIT;
        const confirmed = await document.querySelector('user-confirm-dialog').ask({
            title: tpl(isEdit ? 'pageList.confirmUndoEditTitle' : 'pageList.confirmDiscardTitle', {title: pageLabel(title, path)}),
            body: t(isEdit ? 'pageList.confirmUndoEditBody' : 'pageList.confirmDiscardBody'),
            confirmLabel: t(isEdit ? 'pageList.undoEdit' : 'pageList.discard'),
            cancelLabel: t('global.cancel'),
            danger: true,
        });
        if (!confirmed) {
            return;
        }
        this.#pageListComponent.loading = true;
        try {
            await this.#pageApiAdapter.discardDraft(stagedId);
            const msg = isEdit
                ? t('pageList.notifyEditUndone')
                : t('pageList.notifyDraftDiscarded');
            this.#notificationComponent.show(msg, NotificationType.SUCCESS);
            this.#invalidateLayoutConflictCache();
            await this.loadPages();
        } catch (error) {
            this.#handleError(error);
        } finally {
            this.#pageListComponent.loading = false;
        }
    };

    #onPreviewLinkClicked = () => {
        this.#notificationComponent.show(t('pageForm.previewLinksDisabled'), NotificationType.WARNING);
    };

    #onPublishStaged = async (event) => {
        // Without a path every draft is published; with one, only the draft whose target it is.
        // A blank path still names one draft, so it is refused rather than publishing them all.
        const {path = null, title} = event?.detail ?? {};
        const one = path !== null;
        const confirmed = await document.querySelector('user-confirm-dialog').ask({
            title: one
                ? tpl('pageList.confirmPublishOneTitle', {title: pageLabel(title, path)})
                : t('pageList.confirmPublishTitle'),
            body: t(one ? 'pageList.confirmPublishOneBody' : 'pageList.confirmPublishBody'),
            confirmLabel: t('pageList.confirmPublishAction'),
            cancelLabel: t('global.cancel'),
        });
        if (!confirmed) {
            return;
        }
        // Capture staged paths upfront; after publishing they flip to
        // published and the snapshot is what gets pulsed on the reload.
        let stagedSnapshot = [];
        if (one) {
            stagedSnapshot = [path];
        } else if (Array.isArray(this.#pageListComponent.stagedPagePaths)) {
            stagedSnapshot = [...this.#pageListComponent.stagedPagePaths];
        }
        this.#pageListComponent.loadingMode = 'publishing';
        this.#pageListComponent.publishStage = PublishStage.QUEUED;
        this.#pageListComponent.loading = true;
        try {
            await this.#pageApiAdapter.publishStagedAsync((jobEvent) => {
                const stage = this.#toPublishStage(jobEvent);
                if (stage) {
                    this.#pageListComponent.publishStage = stage;
                }
            }, path);
            this.#pageListComponent.publishStage = PublishStage.COMPLETED;
            this.#notificationComponent.show(t(one ? 'pageList.notifyPublishedOne' : 'pageList.notifyPublishedAll'), NotificationType.SUCCESS);
            this.#invalidateLayoutConflictCache();
            recordJustPublishedPaths(stagedSnapshot);
            this.#announceDraftsPublished();
            // Holds long enough for the ~0.9s success halo + checkmark scale-in
            // to play; loadPages flips loadingMode and would otherwise strip
            // is-success mid-frame.
            await new Promise((resolve) => setTimeout(resolve, 800));
            await this.loadPages();
        } catch (error) {
            this.#pageListComponent.publishStage = PublishStage.FAILED;
            await this.#showPublishFailure(error);
        } finally {
            this.#pageListComponent.loading = false;
            this.#pageListComponent.loadingMode = 'loading';
        }
    };

    /**
     * Tells the user why a publication did not go through. A failure the user can fix (status
     * 400, such as a draft to correct or an address already in use) is shown in its own words;
     * any other failure gets the generic text.
     * @param {any} error
     * @returns {Promise<void>}
     */
    async #showPublishFailure(error) {
        if (error.code === ErrorCode.NO_DRAFTS) {
            // Another tab or session published the drafts first; the list shows what is left.
            this.#notificationComponent.show(errorText(error), NotificationType.WARNING);
            this.#invalidateLayoutConflictCache();
            await this.loadPages();
        } else if (error.status === 409) {
            this.#notificationComponent.show(t('pageList.publishAlreadyRunning'), NotificationType.WARNING);
            this.#invalidateLayoutConflictCache();
            await this.loadPages();
        } else if (error.status === 401) {
            this.#handleError(error);
        } else {
            const text = error.status === 400 ? errorText(error) : t('pageList.progressPublishFailed');
            this.#notificationComponent.show(text, NotificationType.ERROR);
        }
    }

    #toPublishStage(event) {
        const eventName = typeof event?.event === 'string' ? event.event : 'message';
        const status = typeof event?.data?.status === 'string' ? event.data.status.toUpperCase() : '';

        if (eventName === PublishStage.QUEUED || status === PublishJobStatus.QUEUED) return PublishStage.QUEUED;
        if (eventName === PublishStage.RUNNING || status === PublishJobStatus.RUNNING) return PublishStage.RUNNING;
        if (eventName === PublishStage.COMPLETED || status === PublishJobStatus.COMPLETED) return PublishStage.COMPLETED;
        return null;
    }

    #onPageEdit = (event) => {
        const {pagePath, templateName, live, pending} = event.detail;
        const draftParam = live === false ? '&draft=1' : '';
        const pendingParam = pending === true ? '&pending=1' : '';
        location.hash = `#/edit/${encodeURIComponent(templateName)}?path=${encodeURIComponent(pagePath)}${draftParam}${pendingParam}`;
    };

    #onPageDelete = async (event) => {
        const {pagePath, templateName, title} = event.detail;
        const confirmDialog = document.querySelector('user-confirm-dialog');
        const confirmed = await confirmDialog.ask({
            title: tpl('pageList.confirmDeleteTitle', {title: pageLabel(title, pagePath)}),
            body: t('pageList.confirmDeleteBody'),
            confirmLabel: t('pageList.delete'),
            cancelLabel: t('global.cancel'),
            danger: true,
        });
        if (!confirmed) {
            return;
        }

        this.#pageListComponent.loadingMode = 'deleting';
        this.#pageListComponent.loading = true;
        try {
            await this.#pageApiAdapter.deletePage(pagePath, templateName);
            this.#notificationComponent.show(t('pageList.notifyPageDeleted'), NotificationType.SUCCESS);
            this.#invalidateLayoutConflictCache();
            await this.loadPages();
        } catch (error) {
            this.#handleError(error);
        } finally {
            this.#pageListComponent.loadingMode = 'loading';
            this.#pageListComponent.loading = false;
        }
    };

    #handleError(error) {
        if (error?.status === 401) {
            // The toast stays up on the login screen and says why the user was signed out.
            this.#notificationComponent.show(t('global.sessionExpired'), NotificationType.ERROR);
            this.#onSessionExpired();
        } else {
            this.#notificationComponent.show(errorText(error), NotificationType.ERROR);
        }
    }

    #onAssetRequest = async (event) => {
        const {path, img} = event.detail;
        try {
            const blob = await this.#pageApiAdapter.getAsset(path);
            img.src = URL.createObjectURL(blob);
        } catch (error) {
            console.error('Failed to load asset', path, error);
            img.dispatchEvent(new Event('error'));
        }
    };

    #onAssetOpenRequest = async (event) => {
        const {path, filename} = event.detail;
        try {
            const blob = await this.#pageApiAdapter.getAsset(path);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 30_000);
        } catch (error) {
            console.error('Failed to open asset', path, error);
            this.#notificationComponent.show(t('pageForm.downloadFailed'), NotificationType.ERROR);
        }
    };
}
